import React from 'react';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import Portal from '@mui/material/Portal';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalDate } from '@js-joda/core';
import { Dayjs } from 'dayjs';
import 'dayjs/locale/en-gb';
import { useSnackbar } from 'notistack';
import { useQueryClient } from 'react-query';
import { i18n } from 'i18n';
import { ApplySpoolStatusFromDatesResultDto, MINE_MODEL_KEY, useApplySpoolStatusFromDatesMutation } from 'providers/api';
import { useConfirmation } from 'providers/confirm';
import {
  dayJsToLocalDate,
  DEFAULT_SHORT_DATE_NO_TIMEZONE_CONFIG,
  displayTemporal,
  LocalDateToDayJs
} from 'utils';
import { Model } from '../../../hooks/useModelState';
import { spoolDateFilterDay } from '../../../MineModel/spoolDateFilter';
import { TOOLBAR_MENU_Z_INDEX } from './utils';

interface SpoolStatusFromDatesPopoutProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  model: Model;
}

const describeResult = (result: ApplySpoolStatusFromDatesResultDto) => i18n._('{active} spools active, {inactive} inactive ({changed} changed)', {
  active: result.activeCount ?? 0,
  inactive: result.inactiveCount ?? 0,
  changed: result.changedCount ?? 0,
});

const describeApply = (date: string) => [
  i18n._('Sets the status of every spool from its installed and removed dates as of {date}.', { date }),
  i18n._('Spools installed after that date, or removed on or before it, become Inactive; all others become Active.'),
  i18n._('Statuses set by hand are overwritten, and recipes routed over a changed spool may become stale.'),
].join(' ');

const SpoolStatusFromDatesPopout = ({
  open,
  anchorEl,
  onClose,
  model,
}: SpoolStatusFromDatesPopoutProps) => {
  const [asOf, setAsOf] = React.useState<LocalDate>(() => LocalDate.now());
  const [result, setResult] = React.useState<ApplySpoolStatusFromDatesResultDto>();
  const [applying, setApplying] = React.useState(false);
  const [wasOpen, setWasOpen] = React.useState(open);
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirmation();
  const mutation = useApplySpoolStatusFromDatesMutation(model.project.entityId);
  const asOfLabel = displayTemporal(asOf, DEFAULT_SHORT_DATE_NO_TIMEZONE_CONFIG);

  // Viewing the network on a day is the same question this tool answers, so it opens on that day.
  if (open !== wasOpen) {
    setWasOpen(open);
    const filterDay = spoolDateFilterDay(model.spoolDateFilter);
    if (open && filterDay) setAsOf(filterDay);
  }

  const handleDateChange = (date: Dayjs | null) => {
    if (date?.isValid()) {
      setAsOf(dayJsToLocalDate(date));
    }
  };

  const handleApply = async () => {
    // The confirmation dialog sits below the toolbar's popovers, so this one closes first.
    onClose();
    try {
      await confirm({
        variant: 'danger',
        title: i18n._('Set Spool Status From Dates'),
        description: describeApply(asOfLabel),
      });
    } catch {
      return;
    }

    setApplying(true);
    try {
      const data = await mutation.mutateAsync({ projectId: model.project.entityId, asOf });
      // Joins the reload the mutation started, so the overlay stays up until the new statuses are drawn.
      await queryClient.refetchQueries(MINE_MODEL_KEY, { active: true });
      setResult(data);
      enqueueSnackbar(describeResult(data), { variant: 'success' });
    } catch {
      // The global mutation cache has already reported the failure.
    } finally {
      setApplying(false);
    }
  };

  return (
    <>
      <Portal>
        <Backdrop open={applying} sx={{ zIndex: theme.zIndex.modal + 1 }}>
          <Paper
            elevation={3}
            sx={{
              display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2,
            }}
          >
            <CircularProgress size={28} color="secondary" disableShrink />
            <Typography variant="body2">
              {i18n._('Applying spool status as of {date}...', { date: asOfLabel })}
            </Typography>
          </Paper>
        </Backdrop>
      </Portal>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: TOOLBAR_MENU_Z_INDEX }}
      >
        <Box sx={{ p: 1.5, minWidth: 260, maxWidth: 360 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{i18n._('Set Spool Status From Dates')}</Typography>
          <Divider />
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
            <Box sx={{ pt: 1.5 }}>
              <DatePicker
                format="DD/MM/YYYY"
                label={i18n._('As of')}
                value={LocalDateToDayJs(asOf)}
                onChange={handleDateChange}
                slotProps={{
                  textField: { color: 'secondary', fullWidth: true, size: 'small' },
                  popper: { sx: { zIndex: TOOLBAR_MENU_Z_INDEX + 1 } },
                }}
              />
            </Box>
          </LocalizationProvider>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1.5 }}>
            <Button
              size="small"
              variant="contained"
              color="secondary"
              onClick={handleApply}
              disabled={applying}
            >
              {i18n._('Apply')}
            </Button>
          </Box>
          {result && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {describeResult(result)}
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default SpoolStatusFromDatesPopout;
